import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import {
  Brain, Mic, MicOff, Play, Square, Clock, CheckCircle2,
  AlertCircle, Trash2, Mail, FileText, ChevronRight, Loader2,
  Users, ListChecks,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/meeting-brain`;
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

const T = {
  nl: {
    title: 'Meeting Brain',
    subtitle: 'Real-time meeting intelligence — transcriptie, samenvatting & actiepunten',
    newMeeting: 'Nieuwe Meeting',
    startRecording: 'Start Opname',
    stopRecording: 'Stop & Analyseer',
    recording: 'Opnemen...',
    processing: 'Analyseren...',
    completed: 'Voltooid',
    transcript: 'Transcriptie',
    summary: 'Samenvatting',
    decisions: 'Besluiten',
    actionItems: 'Actiepunten',
    followUps: 'Follow-ups',
    topics: 'Onderwerpen',
    email: 'Follow-up E-mail',
    genEmail: 'Genereer E-mail',
    history: 'Meeting Geschiedenis',
    noMeetings: 'Nog geen meetings — voeg er één toe om te beginnen.',
    namePlaceholder: 'Meeting naam (optioneel)',
    participantsPlaceholder: 'Deelnemers (komma-gescheiden)',
    listening: 'Luisteren...',
    delete: 'Verwijderen',
    liveText: 'Live transcriptie',
  },
  en: {
    title: 'Meeting Brain',
    subtitle: 'Real-time meeting intelligence — transcription, summary & action items',
    newMeeting: 'New Meeting',
    startRecording: 'Start Recording',
    stopRecording: 'Stop & Analyze',
    recording: 'Recording...',
    processing: 'Analyzing...',
    completed: 'Completed',
    transcript: 'Transcript',
    summary: 'Summary',
    decisions: 'Decisions',
    actionItems: 'Action Items',
    followUps: 'Follow-ups',
    topics: 'Topics',
    email: 'Follow-up Email',
    genEmail: 'Generate Email',
    history: 'Meeting History',
    noMeetings: 'No meetings yet — add one to get started.',
    namePlaceholder: 'Meeting name (optional)',
    participantsPlaceholder: 'Participants (comma-separated)',
    listening: 'Listening...',
    delete: 'Delete',
    liveText: 'Live transcription',
  },
};

export default function MeetingBrainLivePage() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = T[lang] || T.nl;
  const headers = { Authorization: `Bearer ${token}` };

  const [meetings, setMeetings] = useState([]);
  const [selected, setSelected] = useState(null);
  const [view, setView] = useState('list'); // list, recording, detail

  // Recording state
  const [meetingId, setMeetingId] = useState(null);
  const [meetingName, setMeetingName] = useState('');
  const [participants, setParticipants] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [liveText, setLiveText] = useState('');
  const [fullTranscript, setFullTranscript] = useState('');
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [emailResult, setEmailResult] = useState('');
  const [genEmailLoading, setGenEmailLoading] = useState(false);

  const recognitionRef = useRef(null);
  const pollRef = useRef(null);

  const fetchMeetings = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/list/all`, { headers });
      setMeetings(res.data.meetings || []);
    } catch {}
  }, [token]);

  useEffect(() => { fetchMeetings(); }, [fetchMeetings]);

  const startMeeting = async () => {
    try {
      const res = await axios.post(`${API}/start`, {
        name: meetingName || undefined,
        participants: participants ? participants.split(',').map(p => ((p) == null ? '' : String(p)).trim()) : undefined,
        language: lang,
        source: 'portal',
      }, { headers });
      setMeetingId(res.data.meeting_id);
      setFullTranscript('');
      setLiveText('');
      setView('recording');
      startSpeechRecognition(res.data.meeting_id);
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to start meeting');
    }
  };

  const startSpeechRecognition = (mid) => {
    if (!SpeechRecognition) return;
    const recognition = new SpeechRecognition();
    recognition.lang = lang === 'nl' ? 'nl-NL' : 'en-US';
    recognition.continuous = true;
    recognition.interimResults = true;

    recognition.onstart = () => setIsRecording(true);

    recognition.onresult = (event) => {
      let interim = '';
      let final = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          final += event.results[i][0].transcript + ' ';
        } else {
          interim += event.results[i][0].transcript;
        }
      }
      setLiveText(interim);
      if (((final) == null ? '' : String(final)).trim()) {
        setFullTranscript(prev => prev + final);
        // Send to backend
        axios.post(`${API}/transcribe/text`, {
          meeting_id: mid,
          text: ((final) == null ? '' : String(final)).trim(),
        }, { headers }).catch(() => {});
      }
    };

    recognition.onerror = (e) => {
      if (e.error !== 'aborted' && e.error !== 'no-speech') {
        console.error('Speech error:', e.error);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still recording
      if (recognitionRef.current) {
        try { recognition.start(); } catch {}
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  };

  const stopAndAnalyze = async () => {
    if (recognitionRef.current) {
      recognitionRef.current.onend = null;
      recognitionRef.current.stop();
      recognitionRef.current = null;
    }
    setIsRecording(false);
    setIsAnalyzing(true);

    try {
      await axios.post(`${API}/end/${meetingId}`, { language: lang }, { headers });
      // Poll until completed
      pollRef.current = setInterval(async () => {
        try {
          const res = await axios.get(`${API}/${meetingId}`, { headers });
          if (res.data.status === 'completed' || res.data.status === 'error') {
            clearInterval(pollRef.current);
            setIsAnalyzing(false);
            setSelected(res.data);
            setView('detail');
            fetchMeetings();
          }
        } catch {}
      }, 2000);
    } catch (err) {
      setIsAnalyzing(false);
      alert(err.response?.data?.detail || 'Analysis failed');
    }
  };

  const loadMeeting = async (id) => {
    try {
      const res = await axios.get(`${API}/${id}`, { headers });
      setSelected(res.data);
      setView('detail');
    } catch {}
  };

  const deleteMeeting = async (id) => {
    await axios.delete(`${API}/${id}`, { headers });
    fetchMeetings();
    if (selected?.id === id) { setSelected(null); setView('list'); }
  };

  const generateEmail = async () => {
    if (!selected?.id) return;
    setGenEmailLoading(true);
    try {
      const res = await axios.post(`${API}/${selected.id}/email`, { language: lang, tone: 'professional' }, { headers });
      setEmailResult(res.data.email);
    } catch {}
    setGenEmailLoading(false);
  };

  // Cleanup
  useEffect(() => {
    return () => {
      if (recognitionRef.current) { recognitionRef.current.stop(); }
      if (pollRef.current) { clearInterval(pollRef.current); }
    };
  }, []);

  const analysis = selected?.analysis;

  return (
    <div className="page-container" data-testid="meeting-brain-page">
      <div className="page-header">
        <div className="page-header-icon"><Brain size={24} /></div>
        <div>
          <h1 className="page-title">{t.title}</h1>
          <p className="page-subtitle">{t.subtitle}</p>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '280px 1fr', gap: '1.5rem', marginTop: '1.5rem', minHeight: 500 }}>
        {/* Sidebar */}
        <div>
          {/* New Meeting Form */}
          <div className="card" style={{ padding: '1rem', marginBottom: '1rem' }}>
            <input data-testid="meeting-name" value={meetingName} onChange={e => setMeetingName(e.target.value)} placeholder={t.namePlaceholder}
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '0.4rem', padding: '0.4rem 0.6rem', color: 'var(--text-primary)', fontSize: '0.82rem', marginBottom: '0.5rem' }} />
            <input data-testid="meeting-participants" value={participants} onChange={e => setParticipants(e.target.value)} placeholder={t.participantsPlaceholder}
              style={{ width: '100%', background: 'var(--bg-input)', border: '1px solid var(--border)', borderRadius: '0.4rem', padding: '0.4rem 0.6rem', color: 'var(--text-primary)', fontSize: '0.82rem', marginBottom: '0.75rem' }} />
            <button data-testid="start-meeting-btn" onClick={startMeeting} className="btn-primary" style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              <Play size={14} /> {t.newMeeting}
            </button>
          </div>

          {/* Meeting List */}
          <div className="card" style={{ padding: '0.75rem' }}>
            <div style={{ fontSize: '0.78rem', fontWeight: 600, color: 'var(--text-muted)', padding: '0.25rem 0.5rem', marginBottom: '0.5rem' }}>{t.history}</div>
            {meetings.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--text-muted)', fontSize: '0.82rem' }}>{t.noMeetings}</div>
            ) : (Array.isArray(meetings) ? meetings : []).map(m => (
              <div key={m.id} data-testid={`meeting-${m.id}`}
                onClick={() => loadMeeting(m.id)}
                style={{
                  padding: '0.5rem', borderRadius: '0.4rem', cursor: 'pointer', marginBottom: '0.25rem',
                  background: selected?.id === m.id ? 'rgba(99,102,241,0.1)' : 'transparent',
                  border: `1px solid ${selected?.id === m.id ? 'var(--accent)' : 'transparent'}`,
                  transition: 'all 0.15s',
                }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span style={{ fontSize: '0.82rem', fontWeight: 500, color: 'var(--text-primary)' }}>{m.name}</span>
                  {m.status === 'completed' && <CheckCircle2 size={12} style={{ color: 'var(--success)' }} />}
                  {m.status === 'processing' && <Loader2 size={12} style={{ color: 'var(--accent)', animation: 'spin 1s linear infinite' }} />}
                  {m.status === 'recording' && <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.5s infinite' }} />}
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                  {new Date(m.created_at).toLocaleDateString()} {m.participants?.length > 0 && `/ ${m.participants.length} deelnemers`}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Main Content */}
        <div className="card" style={{ padding: '1.5rem' }}>
          {/* Recording View */}
          {view === 'recording' && (
            <div data-testid="recording-view">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '1.5rem' }}>
                <div style={{ width: 12, height: 12, borderRadius: '50%', background: '#ef4444', animation: 'pulse 1.5s infinite' }} />
                <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>{t.recording}</span>
                <button data-testid="stop-btn" onClick={stopAndAnalyze} className="btn-primary" style={{ marginLeft: 'auto', background: '#ef4444', display: 'flex', alignItems: 'center', gap: 6 }}>
                  <Square size={14} /> {t.stopRecording}
                </button>
              </div>

              {/* Live text */}
              <div style={{ background: 'var(--bg-input)', borderRadius: '0.5rem', padding: '1rem', minHeight: 300, border: '1px solid var(--border)' }}>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontWeight: 600, marginBottom: '0.5rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t.liveText}</div>
                <div style={{ color: 'var(--text-primary)', fontSize: '0.88rem', lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                  {fullTranscript}
                  {liveText && <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>{liveText}</span>}
                  {isRecording && !fullTranscript && !liveText && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--text-muted)' }}>
                      <Mic size={16} style={{ color: '#ef4444' }} /> {t.listening}
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Analyzing */}
          {view === 'recording' && isAnalyzing && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 200, gap: 12 }}>
              <Loader2 size={28} style={{ color: 'var(--accent)', animation: 'spin 1s linear infinite' }} />
              <span style={{ color: 'var(--text-muted)', fontSize: '0.88rem' }}>{t.processing}</span>
            </div>
          )}

          {/* Detail View */}
          {view === 'detail' && selected && (
            <div data-testid="meeting-detail">
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
                <div>
                  <h2 style={{ fontSize: '1.1rem', fontWeight: 700 }}>{analysis?.title || selected.name}</h2>
                  <div style={{ display: 'flex', gap: 12, marginTop: 4, color: 'var(--text-muted)', fontSize: '0.78rem' }}>
                    <span><Clock size={12} style={{ marginRight: 4 }} />{new Date(selected.created_at).toLocaleString()}</span>
                    {selected.participants?.length > 0 && <span><Users size={12} style={{ marginRight: 4 }} />{(Array.isArray(selected.participants) ? selected.participants : []).join(', ')}</span>}
                  </div>
                </div>
                <button onClick={() => deleteMeeting(selected.id)} style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}><Trash2 size={16} /></button>
              </div>

              {analysis ? (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                  {/* Summary */}
                  <Section icon={<FileText size={16} />} title={t.summary}>
                    <p style={{ color: 'var(--text-primary)', fontSize: '0.88rem', lineHeight: 1.6 }}>{analysis.summary}</p>
                  </Section>

                  {/* Action Items */}
                  {analysis.action_items?.length > 0 && (
                    <Section icon={<ListChecks size={16} />} title={t.actionItems}>
                      {(Array.isArray(analysis.action_items) ? analysis.action_items : []).map((a, i) => (
                        <div key={i} style={{ display: 'flex', gap: 8, padding: '0.5rem 0', borderBottom: '1px solid var(--border)' }}>
                          <CheckCircle2 size={14} style={{ color: 'var(--accent)', marginTop: 2, flexShrink: 0 }} />
                          <div>
                            <div style={{ fontSize: '0.85rem', color: 'var(--text-primary)' }}>{a.task}</div>
                            <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2 }}>
                              {a.assignee && <span>{a.assignee}</span>}
                              {a.deadline && <span> / {a.deadline}</span>}
                              {a.priority && <span style={{ marginLeft: 8, padding: '1px 6px', borderRadius: 4, fontSize: '0.68rem', background: a.priority === 'Hoog' || a.priority === 'High' ? 'rgba(239,68,68,0.15)' : 'rgba(99,102,241,0.1)', color: a.priority === 'Hoog' || a.priority === 'High' ? '#ef4444' : 'var(--accent)' }}>{a.priority}</span>}
                            </div>
                          </div>
                        </div>
                      ))}
                    </Section>
                  )}

                  {/* Decisions */}
                  {analysis.decisions?.length > 0 && (
                    <Section icon={<AlertCircle size={16} />} title={t.decisions}>
                      {(Array.isArray(analysis.decisions) ? analysis.decisions : []).map((d, i) => (
                        <div key={i} style={{ padding: '0.4rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem', color: 'var(--text-primary)' }}>
                          <strong>{d.decision}</strong>
                          {d.context && <div style={{ color: 'var(--text-muted)', fontSize: '0.78rem', marginTop: 2 }}>{d.context}</div>}
                        </div>
                      ))}
                    </Section>
                  )}

                  {/* Topics */}
                  {analysis.key_topics?.length > 0 && (
                    <Section icon={<ChevronRight size={16} />} title={t.topics}>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {(Array.isArray(analysis.key_topics) ? analysis.key_topics : []).map((topic, i) => (
                          <span key={i} style={{ padding: '3px 10px', borderRadius: '1rem', background: 'rgba(99,102,241,0.1)', color: 'var(--accent)', fontSize: '0.75rem', fontWeight: 500 }}>{topic}</span>
                        ))}
                      </div>
                    </Section>
                  )}

                  {/* Email */}
                  <Section icon={<Mail size={16} />} title={t.email}>
                    {emailResult ? (
                      <div style={{ background: 'var(--bg-input)', borderRadius: '0.5rem', padding: '1rem', fontSize: '0.85rem', lineHeight: 1.6, whiteSpace: 'pre-wrap', border: '1px solid var(--border)' }}>{emailResult}</div>
                    ) : (
                      <button data-testid="gen-email-btn" onClick={generateEmail} disabled={genEmailLoading} className="btn-primary" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        {genEmailLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Mail size={14} />}
                        {t.genEmail}
                      </button>
                    )}
                  </Section>

                  {/* Transcript */}
                  {selected.transcript_text && (
                    <Section icon={<Mic size={16} />} title={t.transcript} collapsible>
                      <div style={{ background: 'var(--bg-input)', borderRadius: '0.5rem', padding: '1rem', fontSize: '0.82rem', lineHeight: 1.6, maxHeight: 200, overflow: 'auto', border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>
                        {selected.transcript_text}
                      </div>
                    </Section>
                  )}
                </div>
              ) : selected.status === 'error' ? (
                <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
                  <AlertCircle size={24} />
                  <p style={{ marginTop: 8 }}>{selected.error || 'Analysis failed'}</p>
                </div>
              ) : (
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 200, gap: 8 }}>
                  <Loader2 size={20} style={{ animation: 'spin 1s linear infinite', color: 'var(--accent)' }} />
                  <span style={{ color: 'var(--text-muted)' }}>{t.processing}</span>
                </div>
              )}
            </div>
          )}

          {/* Empty state */}
          {view === 'list' && (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: 400, gap: 12, color: 'var(--text-muted)' }}>
              <Brain size={40} style={{ opacity: 0.3 }} />
              <p style={{ fontSize: '0.88rem' }}>{lang === 'nl' ? 'Start een nieuwe meeting of selecteer er een uit de lijst' : 'Start a new meeting or select one from the list'}</p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
      `}</style>
    </div>
  );
}

function Section({ icon, title, children, collapsible = false }) {
  const [open, setOpen] = useState(!collapsible);
  return (
    <div>
      <div onClick={collapsible ? () => setOpen(!open) : undefined}
        style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: open ? '0.5rem' : 0, cursor: collapsible ? 'pointer' : 'default' }}>
        <span style={{ color: 'var(--accent)' }}>{icon}</span>
        <span style={{ fontSize: '0.85rem', fontWeight: 600, color: 'var(--text-primary)' }}>{title}</span>
        {collapsible && <ChevronRight size={14} style={{ marginLeft: 'auto', color: 'var(--text-muted)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />}
      </div>
      {open && children}
    </div>
  );
}
